import { useCallback, useRef, useState } from 'react';
import type { BriefAsset } from '@mybusinessmate/schema';
import { assetFromFile, BRIEF_ASSET_MAX, checkBriefFile, uploadBriefFile, type UploadHandle } from './asset-upload';

export interface UploadItem {
  /** Local id for this row; a finished upload keeps it as the asset's id. */
  id: string;
  name: string;
  size: number;
  contentType: string;
  /** `rejected` was refused before any upload (wrong type, too big): it stays as a row saying why, until removed. */
  status: 'uploading' | 'done' | 'failed' | 'rejected';
  /** 0-1 while uploading. */
  progress: number;
  error?: string;
  /** Local preview for images added in this session. */
  previewUrl?: string;
  asset?: BriefAsset;
  file?: File;
}

type Uploader = (file: File, businessId: string, onProgress: (f: number) => void) => UploadHandle;

/**
 * The brief's files: each is validated, uploaded straight to R2 with progress, and can be retried or removed.
 * `assets` is what the form submits: only files that finished. `busy` is true while any is still uploading, so
 * the form can hold the submit button until nothing is in flight.
 */
export function useAssetUploads(
  businessId: string,
  initial: BriefAsset[] = [],
  upload: Uploader = uploadBriefFile,
  check: (file: File) => string | null = checkBriefFile,
) {
  const [items, setItems] = useState<UploadItem[]>(() =>
    initial.map((asset) => ({
      id: asset.id,
      name: asset.name,
      size: asset.size,
      contentType: asset.contentType,
      status: 'done' as const,
      progress: 1,
      asset,
    })),
  );
  const [notice, setNotice] = useState<string>();
  const handles = useRef(new Map<string, UploadHandle>());
  // The latest list, for callbacks that must count what is attached right now (rejected rows don't count).
  const latest = useRef(items);
  latest.current = items;

  const patch = useCallback((id: string, change: Partial<UploadItem>) => {
    setItems((all) => all.map((item) => (item.id === id ? { ...item, ...change } : item)));
  }, []);

  const start = useCallback(
    (id: string, file: File) => {
      const handle = upload(file, businessId, (progress) => patch(id, { progress }));
      handles.current.set(id, handle);
      handle.promise.then(
        (key) => {
          handles.current.delete(id);
          patch(id, { status: 'done', progress: 1, error: undefined, asset: { ...assetFromFile(file, key), id } });
        },
        (err: unknown) => {
          handles.current.delete(id);
          if (err instanceof DOMException && err.name === 'AbortError') return;
          patch(id, {
            status: 'failed',
            error: (err as { message?: string })?.message || 'Upload failed. Try again.',
          });
        },
      );
    },
    [businessId, patch, upload],
  );

  const addFiles = useCallback(
    (files: File[]) => {
      setNotice(undefined);
      const added: UploadItem[] = [];
      const attached = latest.current.filter((item) => item.status !== 'rejected').length;
      for (const file of files) {
        if (attached + added.filter((item) => item.status !== 'rejected').length >= BRIEF_ASSET_MAX) {
          setNotice(`You can attach up to ${BRIEF_ASSET_MAX} files.`);
          break;
        }
        const problem = check(file);
        if (problem) {
          added.push({
            id: crypto.randomUUID(),
            name: file.name,
            size: file.size,
            contentType: file.type,
            status: 'rejected',
            progress: 0,
            error: problem,
          });
          continue;
        }
        added.push({
          id: crypto.randomUUID(),
          name: file.name,
          size: file.size,
          contentType: file.type,
          status: 'uploading',
          progress: 0,
          file,
          previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        });
      }
      if (added.length === 0) return;
      const accepted = added.filter((item) => item.status === 'uploading');
      setItems((all) => [...all, ...added]);
      for (const item of accepted) start(item.id, item.file!);
    },
    [check, start],
  );

  const remove = useCallback((id: string) => {
    handles.current.get(id)?.abort();
    handles.current.delete(id);
    setItems((all) => {
      const gone = all.find((item) => item.id === id);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return all.filter((item) => item.id !== id);
    });
  }, []);

  const retry = useCallback(
    (id: string) => {
      const item = items.find((i) => i.id === id);
      if (!item?.file) return;
      patch(id, { status: 'uploading', progress: 0, error: undefined });
      start(id, item.file);
    },
    [items, patch, start],
  );

  return {
    items,
    notice,
    addFiles,
    remove,
    retry,
    assets: items.flatMap((i) => (i.status === 'done' && i.asset ? [i.asset] : [])),
    busy: items.some((i) => i.status === 'uploading'),
    failed: items.some((i) => i.status === 'failed'),
  };
}
